// The Buy-Spark modal: list packs, open Stripe's hosted checkout in a new tab,
// then wait here for the Spark to land. Checkout happens off-origin (Stripe,
// then the platform's return page), so this tab cannot be redirected back
// reliably — instead it POLLS the purchase status, re-checks whenever the tab
// regains focus (the natural moment the buyer returns), and offers a manual
// check. That combination is what makes the flow robust for a mini-app that
// does not own the success page.

import {
  listSparkProducts,
  startSparkPurchase,
  getSparkPurchase,
  isPurchaseCredited,
  isPurchaseDead,
  estimateTessellationCount
} from './spark.js';

const el = (id) => document.getElementById(id);

let modal;
let productsEl;
let progressEl;
let progressText;
let checkBtn;
let againBtn;
let errorEl;
let subEl;
let estimateTessellationCost;
let sparkPerTessellation = null;

let pollTimer = null;
let pollDeadline = 0;
let activePurchaseId = null;
let creditedFired = false;

const POLL_MS = 3000;
// A slow buyer should not be abandoned mid-checkout; ten minutes covers a
// distracted tab-switch without polling forever if they walk away.
const POLL_WINDOW_MS = 10 * 60 * 1000;

export function initSparkModal({ estimateCost } = {}) {
  modal = el('spark-modal');
  if (!modal) return;
  productsEl = el('spark-products');
  progressEl = el('spark-progress');
  progressText = el('spark-progress-text');
  checkBtn = el('spark-check');
  againBtn = el('spark-again');
  errorEl = el('spark-error');
  subEl = el('spark-sub');
  estimateTessellationCost = estimateCost;

  el('spark-close').onclick = closeSparkModal;
  el('spark-dismiss').onclick = closeSparkModal;
  checkBtn.onclick = () => {
    if (activePurchaseId) checkStatus();
  };
  againBtn.onclick = () => {
    stopPolling();
    showProducts();
  };

  // Returning to this tab after paying in the Stripe tab is the common path;
  // a focus check turns that into an immediate refresh instead of a wait.
  window.addEventListener('focus', () => {
    if (isOpen() && activePurchaseId && !creditedFired) checkStatus();
  });

  // If the platform ever points a same-origin return page back here, it can
  // announce completion on this channel. Harmless when it never fires —
  // polling already covers the cross-origin case.
  try {
    const channel = new BroadcastChannel('sogni-purchase-status');
    channel.addEventListener('message', (m) => {
      if (m?.data?.type === 'spark-purchase-complete' && activePurchaseId) checkStatus();
    });
  } catch {
    /* BroadcastChannel unsupported: polling still covers it */
  }

  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeSparkModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) closeSparkModal();
  });
}

function isOpen() {
  return modal && !modal.classList.contains('hidden');
}

export async function openSparkModal(subText) {
  if (!modal) return;
  subEl.textContent =
    subText ||
    'Spark pays for each tessellation you roll. Top up below — it lands on your Sogni account.';
  clearError();
  activePurchaseId = null;
  creditedFired = false;
  stopPolling();
  modal.classList.remove('hidden');
  await showProducts();
}

export function closeSparkModal() {
  stopPolling();
  modal?.classList.add('hidden');
}

async function showProducts() {
  progressEl.classList.add('hidden');
  productsEl.classList.remove('hidden');
  productsEl.innerHTML = '<p class="hint">Loading Spark packs…</p>';
  clearError();

  let products;
  try {
    [products, sparkPerTessellation] = await Promise.all([
      listSparkProducts(),
      Promise.resolve(estimateTessellationCost?.()).catch(() => null)
    ]);
  } catch (err) {
    productsEl.innerHTML = '';
    showError(`Could not load Spark packs — ${short(err)}`);
    return;
  }

  if (!products.length) {
    productsEl.innerHTML = '<p class="hint">No Spark packs are available right now.</p>';
    return;
  }

  // Rebuilt from nodes with textContent, never innerHTML with server strings.
  productsEl.innerHTML = '';
  for (const product of products) {
    productsEl.appendChild(productCard(product));
  }
}

function productCard(product) {
  const card = document.createElement('button');
  card.type = 'button';
  card.className = 'spark-pack';

  const amt = document.createElement('span');
  amt.className = 'spark-amt';
  amt.innerHTML =
    '<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><use href="#i-bolt"/></svg>';
  amt.appendChild(document.createTextNode(` ${product.sparkLabel}`));

  const price = document.createElement('span');
  price.className = 'spark-price';
  price.textContent = product.priceLabel;

  card.append(amt, price);

  const estimate = estimateTessellationCount(product.spark, sparkPerTessellation);
  if (estimate != null) {
    const renders = document.createElement('span');
    renders.className = 'spark-renders';
    renders.textContent = `≈${estimate.toLocaleString()} tessellation${estimate === 1 ? '' : 's'}`;
    card.appendChild(renders);
  }

  if (product.discount > 0) {
    const save = document.createElement('span');
    save.className = 'spark-save';
    save.textContent = `Save ${product.discount}%`;
    card.appendChild(save);
  }
  if (product.description) card.title = product.description;

  card.onclick = () => beginPurchase(product);
  return card;
}

async function beginPurchase(product) {
  clearError();
  setCardsDisabled(true);
  let intent;
  try {
    intent = await startSparkPurchase(product.id);
  } catch (err) {
    setCardsDisabled(false);
    showError(`Could not start checkout — ${short(err)}`);
    return;
  }
  setCardsDisabled(false);

  activePurchaseId = intent.purchaseId;
  creditedFired = false;

  // Open checkout in a new tab so this one can keep polling and the buyer has
  // somewhere to return to. Not the 'noopener' feature — with it, window.open
  // returns null even on success, which would defeat the blocked-popup check;
  // instead sever the opener by hand, which both protects against reverse
  // tabnabbing and lets a null return actually mean "blocked".
  const opened = window.open(intent.url, '_blank');
  if (opened) opened.opener = null;
  showProgress('waiting', { url: intent.url, popupBlocked: !opened });
  startPolling();
  checkStatus();
}

async function checkStatus() {
  if (!activePurchaseId) return;
  setBusy(true);
  let status;
  try {
    status = await getSparkPurchase(activePurchaseId);
  } catch (err) {
    setBusy(false);
    showError(`Could not check that purchase — ${short(err)}`);
    return;
  }
  setBusy(false);

  const state = status?.status;
  if (isPurchaseCredited(state)) {
    stopPolling();
    showProgress('credited');
    if (!creditedFired) {
      creditedFired = true;
      // The app owns the account chip; let it refresh the balance.
      window.dispatchEvent(
        new CustomEvent('spark-purchased', {
          detail: { purchaseId: activePurchaseId }
        })
      );
    }
  } else if (isPurchaseDead(state)) {
    stopPolling();
    showProgress('expired');
  }
  // Otherwise still waiting: the next poll tick or a manual check will catch it.
}

function showProgress(state, checkout) {
  productsEl.classList.add('hidden');
  progressEl.classList.remove('hidden');
  progressText.innerHTML = '';
  checkBtn.classList.toggle('hidden', state !== 'waiting');
  againBtn.classList.toggle('hidden', state === 'waiting');
  againBtn.textContent = state === 'expired' ? 'Start new checkout' : 'Buy more Spark';

  const line = document.createElement('span');
  if (state === 'credited') {
    line.textContent = '🎉 Thank you! Your Spark has been added to your balance.';
  } else if (state === 'expired') {
    line.textContent =
      'That checkout expired before payment completed. Start a new one to add Spark.';
  } else {
    line.textContent =
      'Complete your purchase in the Stripe tab. ' +
      'Your Spark lands here automatically — this page is checking for it.';
  }
  progressText.appendChild(line);

  if (state === 'waiting' && checkout?.url) {
    progressText.appendChild(document.createElement('br'));
    const link = document.createElement('a');
    link.href = checkout.url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.className = 'spark-link';
    link.textContent = checkout.popupBlocked
      ? 'Open the Stripe checkout ↗'
      : 'Reopen the Stripe checkout ↗';
    progressText.appendChild(link);
  }
}

function startPolling() {
  stopPolling();
  pollDeadline = Date.now() + POLL_WINDOW_MS;
  pollTimer = setInterval(() => {
    if (Date.now() > pollDeadline || !activePurchaseId) {
      stopPolling();
      return;
    }
    checkStatus();
  }, POLL_MS);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function setBusy(busy) {
  if (checkBtn.classList.contains('hidden')) return;
  checkBtn.disabled = busy;
  checkBtn.textContent = busy ? 'Checking…' : 'Check status';
}

function setCardsDisabled(disabled) {
  productsEl.classList.toggle('busy', disabled);
  for (const card of productsEl.querySelectorAll('.spark-pack')) card.disabled = disabled;
}

function short(err) {
  return String(err?.message || err || 'unknown error').slice(0, 140);
}
function showError(msg) {
  errorEl.textContent = msg;
  errorEl.classList.remove('hidden');
}
function clearError() {
  errorEl.textContent = '';
  errorEl.classList.add('hidden');
}
